import { useEffect, useRef, useState } from 'react';

interface UseSyncedVisitFilterDraftOptions<T> {
  value: T | null | undefined;
  onChange: (value: T | null) => void;
  isEmpty: (draft: T | null) => boolean;
  isComplete: (draft: T | null) => boolean;
}

/**
 * Keeps in-progress filter edits in local state. Commits to parent (URL) only when
 * isComplete passes, or clears parent when isEmpty.
 */
export const useSyncedVisitFilterDraft = <T,>({
  value,
  onChange,
  isEmpty,
  isComplete,
}: UseSyncedVisitFilterDraftOptions<T>) => {
  const [draft, setDraft] = useState<T | null>(value ?? null);
  const lastCommittedRef = useRef<string | null>(
    value != null ? JSON.stringify(value) : null
  );

  useEffect(() => {
    const serialized = value != null ? JSON.stringify(value) : null;
    if (serialized !== lastCommittedRef.current) {
      lastCommittedRef.current = serialized;
      setDraft(value ?? null);
    }
  }, [value]);

  const applyDraft = (next: T | null) => {
    setDraft(next);

    if (isEmpty(next)) {
      lastCommittedRef.current = null;
      onChange(null);
      return;
    }

    if (isComplete(next)) {
      const serialized = JSON.stringify(next);
      lastCommittedRef.current = serialized;
      onChange(next);
    }
  };

  const patchDraft = (patch: Partial<T>, merge: (current: T | null, patch: Partial<T>) => T) => {
    applyDraft(merge(draft, patch));
  };

  return { draft, patchDraft };
};
